import SegmentedButton from "@/components/SegmentedButton";
import Chip, { ChipSet } from "@/components/Chip";
import DateSelect from "@/components/DateSelect";
import classNames from "classnames";
import { DateTime } from "luxon";
import { useIsCompactLayout } from "@/hooks/useIsCompactLayout";
import {
  anchorForModeChange,
  buildCustomPeriod,
  buildPeriod,
  currentPeriod,
  customPeriodFromWindow,
  filmstripLabel,
  isCurrentPeriod,
  neighboringPeriods,
  type PeriodMode,
  type PeriodWindow
} from "./periodWindow";

const MODE_OPTIONS: { label: string; value: PeriodMode }[] = [
  { label: "Week", value: "week" },
  { label: "Month", value: "month" },
  { label: "Year", value: "ytd" },
  { label: "Custom", value: "custom" }
];

const FILMSTRIP_SCROLL_CLASS =
  "overflow-x-auto overscroll-x-contain [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden";

export type DateRangeScrollerProps = {
  value: PeriodWindow;
  onChange: (period: PeriodWindow) => void;
};

function DateRangeDivider() {
  return (
    <div
      className="h-6 w-px shrink-0 bg-sys-brand-outline-variant"
      aria-hidden="true"
      data-testid="date-range-scroller-divider"
    />
  );
}

function DateRangeChip({
  label,
  selected,
  ariaLabel,
  onClick
}: {
  label: string;
  selected: boolean;
  ariaLabel: string;
  onClick: () => void;
}) {
  return <Chip label={label} selected={selected} showCheckmark={false} aria-label={ariaLabel} onClick={onClick} />;
}

export default function DateRangeScroller({ value, onChange }: DateRangeScrollerProps) {
  const { isCompactLayout, isMobile } = useIsCompactLayout();
  const labelDensity = isMobile ? "narrow" : isCompactLayout ? "compact" : "full";
  const isCustom = value.mode === "custom";
  // Year pills are short — always show two prior years. Week/month stay leaner on compact.
  const neighborCount = value.mode === "ytd" ? 2 : isMobile || isCompactLayout ? 1 : 2;
  const neighbors = isCustom
    ? []
    : neighboringPeriods(value, neighborCount).filter((neighbor) => !isCurrentPeriod(neighbor));
  const viewingCurrent = !isCustom && isCurrentPeriod(value);
  const current = currentPeriod(isCustom ? "month" : value.mode);
  const currentPillLabel = filmstripLabel(current, labelDensity);

  const handleModeChange = (mode: PeriodMode) => {
    if (mode === "custom") {
      onChange(customPeriodFromWindow(value));
      return;
    }
    if (mode === "week" || mode === "month" || mode === "ytd") {
      onChange(buildPeriod(mode, anchorForModeChange(mode, value)));
    }
  };

  const handleCustomRangeChange = (
    date: { startDate: Date | string | null; endDate: Date | string | null } | string | null
  ) => {
    if (!date || typeof date === "string") return;
    if (!date.startDate || !date.endDate) return;

    const start =
      date.startDate instanceof Date
        ? DateTime.fromJSDate(date.startDate)
        : DateTime.fromISO(date.startDate.toString());
    const end =
      date.endDate instanceof Date ? DateTime.fromJSDate(date.endDate) : DateTime.fromISO(date.endDate.toString());

    if (!start.isValid || !end.isValid) return;
    onChange(buildCustomPeriod(start, end));
  };

  return (
    <div
      className={classNames("flex min-w-0 w-full gap-2", isCompactLayout ? "flex-col items-start" : "items-center")}
      data-testid="date-range-scroller"
    >
      <div className={classNames("shrink-0", isCompactLayout && "w-full")}>
        <SegmentedButton
          ariaLabel="Period mode"
          options={MODE_OPTIONS}
          value={value.mode}
          fullWidth={isCompactLayout}
          onChange={(mode) => {
            if (mode === "week" || mode === "month" || mode === "ytd" || mode === "custom") {
              handleModeChange(mode);
            }
          }}
        />
      </div>
      {!isCompactLayout && !isCustom && <DateRangeDivider />}
      {isCustom ? (
        <div className={classNames("min-w-0", isCompactLayout ? "w-full" : "w-[20rem] max-w-full")}>
          <DateSelect
            label="Date Range"
            placeholder="Select date range"
            value={{
              startDate: value.start.toISODate(),
              endDate: value.end.toISODate()
            }}
            useDefaultShortcuts
            useModal={isCompactLayout}
            maxDate={new Date()}
            popoverDirection="down"
            hideClearButton
            allowClear={false}
            onChange={handleCustomRangeChange}
          />
        </div>
      ) : (
        <div
          className={classNames(
            "flex min-w-0 items-center gap-2",
            isCompactLayout ? "w-full" : "flex-1",
            FILMSTRIP_SCROLL_CLASS
          )}
        >
          <div
            className="flex shrink-0 items-center gap-2"
            data-testid="date-range-scroller-filmstrip"
            role="group"
            aria-label={value.mode === "ytd" ? "Year" : "Period"}
          >
            <ChipSet>
              {neighbors.map((neighbor) => {
                const selected = neighbor.anchor.hasSame(
                  value.anchor,
                  value.mode === "week" ? "week" : value.mode === "month" ? "month" : "year"
                );
                return (
                  <DateRangeChip
                    key={`${neighbor.mode}-${neighbor.anchor.toISODate()}`}
                    label={filmstripLabel(neighbor, labelDensity)}
                    selected={selected}
                    ariaLabel={neighbor.label}
                    onClick={() => onChange(neighbor)}
                  />
                );
              })}
            </ChipSet>
            <DateRangeDivider />
            <ChipSet>
              <DateRangeChip
                label={currentPillLabel}
                selected={viewingCurrent}
                ariaLabel={`Jump to current period: ${current.label}`}
                onClick={() => onChange(current)}
              />
            </ChipSet>
          </div>
        </div>
      )}
      <p className="sr-only" aria-live="polite">
        {value.label}
      </p>
    </div>
  );
}
